import React, { useState, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface InputProps {
  style?: React.CSSProperties
}

interface MyTextAreaProps {
  className?: string
  value: string
  onChange:
    | ((e: { target: { value: string } }) => void)
    | ((value: string) => void)
  label?: string
  multiline?: boolean
  name?: string
  required?: boolean
  InputProps?: InputProps
  minLines?: number
  placeholder?: string
  minLength?: number
  maxLength?: number
  variant?: 'white' | 'grisclair' | 'grisclairBorder'
}

const MyTextArea: React.FC<MyTextAreaProps> = ({
  className = '',
  value,
  onChange,
  label,
  name,
  required = false,
  InputProps,
  minLines = 3,
  placeholder = '',
  minLength = 0,
  maxLength,
  variant = 'white',
}) => {
  const { t } = useTranslation()
  const [currentValue, setCurrentValue] = useState(value)

  const [isFocused, setIsFocused] = useState(false)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setCurrentValue(value)
  }, [value])

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto'
      textareaRef.current.style.height = `${Math.max(
        minLines * 40 + 32,
        textareaRef.current.scrollHeight,
      )}px`
    }
  }, [currentValue, minLines])

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value

    if (maxLength && newValue.length > maxLength) {
      const truncatedValue = newValue.slice(0, maxLength)
      setCurrentValue(truncatedValue)
      if (typeof onChange === 'function') {
        if (onChange.length === 1) {
          ;(onChange as (value: string) => void)(truncatedValue)
        } else {
          ;(onChange as (e: { target: { value: string } }) => void)({
            target: { value: truncatedValue },
          })
        }
      }
      return
    }

    setCurrentValue(newValue)
    if (typeof onChange === 'function') {
      if (onChange.length === 1) {
        ;(onChange as (value: string) => void)(newValue)
      } else {
        ;(onChange as (e: { target: { value: string } }) => void)({
          target: { value: newValue },
        })
      }
    }
  }

  const handleFocus = () => {
    setIsFocused(true)
  }

  const handleBlur = () => {
    setIsFocused(false)
  }

  const getBackgroundColor = () => {
    if (required && !currentValue) return ''
    if (variant === 'grisclair')
      return 'bg-grisclair rounded-md hover:bg-grisclair2'
    if (variant === 'grisclairBorder')
      return 'bg-grisclair border-2 border-anthracite rounded-b-md hover:bg-grisclair2'
    return 'bg-white rounded-md hover:bg-gray-50'
  }

  const getTextareaBackgroundColor = () => {
    switch (variant) {
      case 'grisclair':
        return 'bg-grisclair rounded-lg hover:bg-grisclair2 border-none'
      case 'grisclairBorder':
        return 'bg-grisclair hover:bg-grisclair2'
      default:
        return 'bg-white hover:bg-gray-50 border-none'
    }
  }

  const getPlaceholder = () => {
    if (!required) return placeholder
    return `${placeholder}${placeholder ? ' *' : '*'}`
  }

  const getTextareaClasses = () => {
    const baseClasses =
      'w-full outline-none resize-none transition-all duration-200'
    const paddingClasses = 'px-4 py-2'
    const responsiveClasses = 'text-base sm:text-lg'
    const variantClasses = getTextareaBackgroundColor()

    return `${baseClasses} ${paddingClasses} ${responsiveClasses} ${variantClasses}`
  }

  const baseHeight =
    InputProps?.style?.height || `calc(${minLines} * 40px + 2 * 16px)`

  return (
    <div
      ref={containerRef}
      className='relative my-textarea-container w-full rounded-md mb-8'
    >
      <div
        className={`relative flex w-full transition-all ${getBackgroundColor()} ${className}`}
        style={{
          minHeight: baseHeight,
          ...InputProps?.style,
        }}
      >
        {label && (
          <label
            htmlFor={`textarea-${name || label}`}
            className={`transform transition-all duration-300 z-0 cursor-pointer
                ${
                  isFocused || currentValue
                    ? 'transform transition-all duration-300 z-10 cursor-pointer absolute -translate-y-1/2 -translate-x-1/2 left-1/2 ml-0 w-auto font-semibold text-xs text-anthracite/60 bg-transparent rounded-t-md px-2 py-0'
                    : 'absolute inset-0 flex items-start pt-4 pl-2 text-gray-400 text-sm sm:text-base'
                }`}
          >
            {t(label)}
            {required && ' *'}
          </label>
        )}

        <div className='relative w-full'>
          <textarea
            id={`textarea-${name || label}`}
            ref={textareaRef}
            name={name}
            className={getTextareaClasses()}
            value={currentValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            placeholder={getPlaceholder()}
            maxLength={maxLength}
            style={{
              minHeight: baseHeight,
              alignItems: 'flex-start',
              fontSize: 'inherit',
            }}
          />
        </div>
      </div>
      {(minLength > 0 || maxLength) && (
        <div
          className={`absolute right-2 bottom-2 text-xs ${
            maxLength && currentValue.length >= maxLength
              ? 'text-redpink'
              : currentValue.length < minLength
              ? 'text-redpink'
              : 'text-gray-500'
          }`}
        >
          {currentValue.length}
          {minLength > 0 && ` / ${minLength}`}
          {maxLength && ` (max ${maxLength})`}
        </div>
      )}
    </div>
  )
}

export default MyTextArea
